import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useBranding } from '../context/BrandingContext';
import { useAuth } from '../context/AuthContext';
import {
  ArrowsRightLeftIcon,
  BanknotesIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronDownIcon,
  CreditCardIcon,
  DocumentTextIcon,
  HomeIcon,
  TruckIcon,
  UserGroupIcon,
  UserIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

type SidebarLink = {
  path: string;
  label: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement>>;
  comingSoon?: boolean;
};

type SidebarProps = {
  collapsed: boolean;
  onToggleCollapse: () => void;
};

function PlatformSidebar({ collapsed, onToggleCollapse }: SidebarProps) {
  const location = useLocation();
  const branding = useBranding();
  const { user } = useAuth();
  const [operationsOpen, setOperationsOpen] = useState(true);
  const [billingOpen, setBillingOpen] = useState(true);

  const isPlatformHost =
    typeof window !== 'undefined' &&
    window.location?.hostname?.toLowerCase().startsWith('globaladmin.');
  const showPlatformNav =
    isPlatformHost && Boolean((user as any)?.is_platform_admin);

  const tenantInfo = useMemo(() => {
    const hostname =
      typeof window !== 'undefined'
        ? window.location.hostname.toLowerCase()
        : '';
    const slug = hostname.includes('.') ? hostname.split('.')[0] : 'local';
    const isGlobalAdmin = slug === 'globaladmin';
    const companyName = branding.app_name?.trim();

    return {
      label: isGlobalAdmin ? 'Platform Account' : 'Company',
      value: isGlobalAdmin
        ? companyName || 'ModalOS Platform'
        : companyName || slug || 'local',
    };
  }, [branding.app_name]);

  const topLinks = useMemo<SidebarLink[]>(() => {
    if (showPlatformNav) {
      return [{ path: '/admin', label: 'Home', icon: HomeIcon }, { path: '/admin/ai-access', label: 'AI Access', icon: WrenchScrewdriverIcon }, { path: '/platform/booking-assistant', label: 'Booking assistant', icon: WrenchScrewdriverIcon }];
    }

    return [{ path: '/admin', label: 'Home', icon: HomeIcon }, { path: '/admin/ai-access', label: 'AI Access', icon: WrenchScrewdriverIcon }];
  }, [showPlatformNav]);

  const operationLinks = useMemo<SidebarLink[]>(() => {
    if (showPlatformNav) {
      return [
        {
          path: '/platform/companies',
          label: 'Companies',
          icon: BuildingOfficeIcon,
        },
        {
          path: '/platform/support-staff',
          label: 'Support Staff',
          icon: WrenchScrewdriverIcon,
        },
        { path: '/platform/users', label: 'Users', icon: UserGroupIcon },
      ];
    }

    return [
      { path: '/admin/customers', label: 'Customers', icon: UsersIcon },
      { path: '/admin/accounts', label: 'Accounts', icon: BuildingOfficeIcon },
      { path: '/admin/quotes', label: 'Quotes', icon: DocumentTextIcon },
      {
        path: '/admin/reservations',
        label: 'Reservations',
        icon: DocumentTextIcon,
      },
      { path: '/admin/farm-outs', label: 'Farm-outs', icon: CalendarDaysIcon },
      { path: '/admin/dispatch', label: 'Dispatch', icon: CalendarDaysIcon },
      { path: '/admin/vehicles', label: 'Vehicles', icon: TruckIcon },
      {
        path: '/admin/drivers',
        label: 'Drivers',
        icon: UserIcon,
      },
      { path: '/admin/users', label: 'Users', icon: UserGroupIcon },
    ];
  }, [showPlatformNav]);

  const billingLinks = useMemo<SidebarLink[]>(() => {
    if (showPlatformNav) {
      return [
        {
          path: '/platform/billing',
          label: 'Billing Overview',
          icon: CreditCardIcon,
        },
        {
          path: '/platform/invoices',
          label: 'Platform Invoices',
          icon: DocumentTextIcon,
        },
      ];
    }

    return [
      {
        path: '/admin/billing',
        label: 'Billing Overview',
        icon: BanknotesIcon,
      },
      { path: '/admin/payments', label: 'Payments', icon: CreditCardIcon },
      { path: '/admin/invoices', label: 'Invoices', icon: DocumentTextIcon },
      {
        path: '/admin/invoice-import',
        label: 'Invoice Import',
        icon: ArrowsRightLeftIcon,
      },
    ];
  }, [showPlatformNav]);

  const isPathActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const sectionButtonClass =
    'w-full flex items-center rounded-xl px-3 py-2.5 text-sm font-medium tracking-[-0.01em] transition-all duration-150';
  const releaseLabel = (
    process.env.REACT_APP_VERSION ||
    process.env.REACT_APP_COMMIT_SHA ||
    'dev'
  ).toString();

  return (
    <aside
      className={`admin-shell-surface hidden md:flex fixed top-0 left-0 z-40 h-screen overflow-y-auto transition-[width] duration-200 ${
        collapsed ? 'w-24' : 'w-72'
      }`}
      aria-label="Primary sidebar navigation"
    >
      <div className="w-full p-4">
        <div className="mb-4 flex items-center justify-between">
          <Link
            to="/admin"
            className={`flex items-center ${
              collapsed ? 'justify-center' : 'gap-2'
            } min-w-0`}
            title="MODAL"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-xs font-semibold text-white">
              M
            </span>
            {!collapsed && (
              <span className="truncate text-base font-semibold tracking-editorial text-on-surface">
                MODAL
              </span>
            )}
          </Link>

          <button
            type="button"
            onClick={onToggleCollapse}
            className="admin-icon-button rounded-xl p-2"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <ChevronDoubleRightIcon className="h-5 w-5" />
            ) : (
              <ChevronDoubleLeftIcon className="h-5 w-5" />
            )}
          </button>
        </div>

        {!collapsed && (
          <div className="admin-subpanel mb-4 rounded-2xl px-3 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-on-surface-variant">
              {tenantInfo.label}
            </div>
            <div className="mt-1 truncate text-sm font-semibold text-on-surface">
              {tenantInfo.value}
            </div>
          </div>
        )}
        <div className="space-y-1">
          {topLinks.map(({ path, label, icon: Icon }) => {
            const active = isPathActive(path);
            return (
              <Link
                key={path}
                to={path}
                title={collapsed ? label : undefined}
                className={`${sectionButtonClass} ${
                  active ? 'admin-nav-link-active' : 'admin-nav-link'
                } ${collapsed ? 'justify-center' : 'justify-start gap-3'}`}
              >
                <Icon className="h-5 w-5 shrink-0" />
                {!collapsed && <span className="font-medium">{label}</span>}
              </Link>
            );
          })}
        </div>

        <div className="mt-6">
          <button
            type="button"
            onClick={() => setOperationsOpen((current) => !current)}
            className="admin-nav-link flex w-full items-center justify-between rounded-xl px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em]"
          >
            {!collapsed && <span>Operations</span>}
            <ChevronDownIcon
              className={`h-4 w-4 transition-transform ${
                operationsOpen ? 'rotate-180' : ''
              } ${collapsed ? 'mx-auto' : ''}`}
            />
          </button>

          {operationsOpen && (
            <div className="mt-1 space-y-1">
              {operationLinks.map(({ path, label, icon: Icon, comingSoon }) => {
                const active = isPathActive(path);
                const className = `${sectionButtonClass} ${
                  active ? 'admin-nav-link-active' : 'admin-nav-link'
                } ${collapsed ? 'justify-center' : 'justify-start gap-3'} ${
                  comingSoon ? 'opacity-65 cursor-not-allowed' : ''
                }`;
                const content = (
                  <>
                    <Icon className="h-5 w-5 shrink-0" />
                    {!collapsed && (
                      <span className="flex min-w-0 items-center gap-2">
                        <span>{label}</span>
                        {comingSoon && (
                          <span className="rounded-full border border-outline/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-on-surface-variant">
                            Soon
                          </span>
                        )}
                      </span>
                    )}
                  </>
                );

                if (comingSoon) {
                  return (
                    <span
                      key={path}
                      title={collapsed ? `${label} (Coming soon)` : undefined}
                      className={className}
                    >
                      {content}
                    </span>
                  );
                }

                return (
                  <Link
                    key={path}
                    to={path}
                    title={collapsed ? label : undefined}
                    className={className}
                  >
                    {content}
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-4">
          <button
            type="button"
            onClick={() => setBillingOpen((current) => !current)}
            className="admin-nav-link flex w-full items-center justify-between rounded-xl px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em]"
          >
            {!collapsed && <span>Billing</span>}
            <ChevronDownIcon
              className={`h-4 w-4 transition-transform ${
                billingOpen ? 'rotate-180' : ''
              } ${collapsed ? 'mx-auto' : ''}`}
            />
          </button>

          {billingOpen && (
            <div className="mt-1 space-y-1">
              {billingLinks.map(({ path, label, icon: Icon }) => {
                const active = isPathActive(path);
                return (
                  <Link
                    key={path}
                    to={path}
                    title={collapsed ? label : undefined}
                    className={`${sectionButtonClass} ${
                      active ? 'admin-nav-link-active' : 'admin-nav-link'
                    } ${collapsed ? 'justify-center' : 'justify-start gap-3'}`}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    {!collapsed && <span>{label}</span>}
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {!collapsed && (
          <div className="mt-8 border-t border-outline/40 pt-3 text-[11px] text-on-surface-variant">
            Release {releaseLabel.slice(0, 12)}
          </div>
        )}
      </div>
    </aside>
  );
}

export default PlatformSidebar;
